import React, { useState } from 'react';
import { notUndefined } from '../../../../../../../../shared/lib/functional';
import { isValidEmail } from '../../../../../../../../shared/lib/utils';
import CartSummary from '../../components/CartSummary';
import { TextInput } from '../../components/TextInput';
import { useForcedCartCache } from '../../context/CartCacheContext';
import { StepPropsBase } from '../Reserve';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import phone from 'phone';

type PaymentViewProps = StepPropsBase<void, Promise<void>>;

export default function PaymentView({ onBack, onNext }: PaymentViewProps) {
  const { cart, saveCustomer, passiveUpdateCustomer } = useForcedCartCache();
  const { customer } = cart;

  const [loading, setLoading] = useState(false);
  const [touchedDict, setTouchedDict] = useState<Record<string, boolean>>({
    firstName: (customer?.firstName?.length ?? 0) > 0,
    lastName: (customer?.lastName?.length ?? 0) > 0,
    email: (customer?.email?.length ?? 0) > 0,
    phone: (customer?.phone?.length ?? 0) > 0,
  });

  const parsedPhone = phone(customer?.phone ?? '');

  const isFirstNameValid = (customer?.firstName?.length ?? 0) > 0;
  const isLastNameValid = (customer?.lastName?.length ?? 0) > 0;
  const isEmailValid = isValidEmail(customer?.email ?? '');
  const isPhoneValid = !!(customer?.phone && parsedPhone.isValid);

  const canContinue = isFirstNameValid && isLastNameValid && isEmailValid && isPhoneValid;

  function touch(field: string) {
    setTouchedDict(prev => ({ ...prev, [field]: true }));
  }

  async function handleOnNext() {
    setLoading(true);

    try {
      if (!canContinue) return;
      await saveCustomer();
      await onNext?.();
    } catch (err) {
      setLoading(false);
    }
  }

  const numOfItems = cart.workingDrafts.length;

  return (
    <TwoColumnLayout
      title={`Your Cart — ${numOfItems} item(s)`}
      subtitle="Payment"
      aside={
        <CartSummary
          onBack={() => onBack?.()}
          onNext={handleOnNext}
          nextDisabled={!canContinue || loading}
          loading={loading}
          nextLabel="Pay & Book"
        />
      }
    >
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="grid grid-cols-1 gap-6 p-6">
          <div className="font-bold text-xl text-accent">Buyer Information</div>
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-bold uppercase text-xs">
                  First Name <span className="text-error">*</span>
                </label>
                <TextInput
                  onBlur={() => touch('firstName')}
                  error={
                    touchedDict['firstName']
                      ? [!isFirstNameValid ? 'This field is required' : undefined].filter(
                          notUndefined
                        )
                      : undefined
                  }
                  aria-label="First Name"
                  type="text"
                  value={customer?.firstName ?? ''}
                  onChange={e => passiveUpdateCustomer('firstName', e.target.value)}
                />
              </div>
              <div>
                <label className="font-bold uppercase text-xs">
                  Last Name <span className="text-error">*</span>
                </label>
                <TextInput
                  onBlur={() => touch('lastName')}
                  error={
                    touchedDict['lastName']
                      ? [!isLastNameValid ? 'This field is required' : undefined].filter(
                          notUndefined
                        )
                      : undefined
                  }
                  aria-label="Last Name"
                  type="text"
                  value={customer?.lastName ?? ''}
                  onChange={e => passiveUpdateCustomer('lastName', e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="font-bold uppercase text-xs">
                Email <span className="text-error">*</span>
              </label>
              <TextInput
                onBlur={() => touch('email')}
                error={
                  touchedDict['email']
                    ? [
                        !customer?.email
                          ? 'This field is required'
                          : !isEmailValid
                            ? 'Please enter a valid email address'
                            : undefined,
                      ].filter(notUndefined)
                    : undefined
                }
                aria-label="Email"
                type="email"
                value={customer?.email ?? ''}
                onChange={e => passiveUpdateCustomer('email', e.target.value)}
              />
            </div>
            <div>
              <label className="font-bold uppercase text-xs">
                Phone Number <span className="text-error">*</span>
              </label>
              <TextInput
                onBlur={() => touch('phone')}
                error={
                  touchedDict['phone']
                    ? [!isPhoneValid ? 'Please enter a valid phone number' : undefined].filter(
                        notUndefined
                      )
                    : undefined
                }
                aria-label="Phone Number"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={customer?.phone ?? ''}
                onChange={e => passiveUpdateCustomer('phone', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </TwoColumnLayout>
  );
}
